import React from "react";
import { useForm, Controller, SubmitHandler } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import Label from "../ui/label.tsx";
import Input from "../ui/input.tsx";
import Button from "../ui/button.tsx";
import { SimpleSelect } from "../ui/select-shadcn.tsx";

const amountSchema = z.preprocess((v) => {
  if (v === "" || v == null) return undefined;
  const n = typeof v === "string" ? Number(v) : v;
  return Number.isFinite(n as number) ? n : v;
}, z.number({ required_error: "Amount is required", invalid_type_error: "Amount must be a number" }).positive("Amount must be greater than 0"));

export const subscriptionSchema = z.object({
  title: z.string().min(1, "Title is required"),
  type: z.enum(["expense", "income"]),
  description: z.string().optional().default(""),
  amount: amountSchema,
  category: z.string().min(1, "Category is required"),
  startDate: z.string().min(1, "Start date is required"),
  endDate: z.string().optional().default(""),
  currency: z.string().min(1, "Currency is required"),
  notes: z.string().optional().default(""),
  recurrenceType: z.enum(["weekly", "monthly", "yearly", "custom"]),
  isActive: z.boolean().optional().default(true),
});

// Derive types from the schema
type FormInput = z.input<typeof subscriptionSchema>;
type FormOutput = z.output<typeof subscriptionSchema>;

interface AddSubscriptionFormProps {
  onSubmit: SubmitHandler<FormOutput>;
  onError?: (err: any) => void;
  defaultValues?: Partial<FormInput>;
}

export function AddSubscriptionForm({
  onSubmit,
  onError,
  defaultValues,
}: AddSubscriptionFormProps) {
  const defaultCurrency = (() => {
    try {
      return localStorage.getItem("pref:baseCurrency") || "AED";
    } catch {
      return "AED";
    }
  })();

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
  } = useForm<FormInput, any, FormOutput>({
    // Key part: resolver + Input/Output generics
    resolver: zodResolver(subscriptionSchema),
    defaultValues: {
      title: "",
      type: "expense",
      // Input can be "", Zod preprocess will coerce on validation
      description: "",
      amount: 0,
      category: "subscriptions",
      startDate: new Date().toISOString().slice(0, 10),
      endDate: undefined,
      currency: defaultCurrency,
      notes: "",
      recurrenceType: "monthly",
      isActive: true,
      ...(defaultValues || {}),
    },
  });

  return (
    <form onSubmit={handleSubmit(onSubmit, onError)} className="space-y-6">
      {/* Title */}
      <div>
        <Label htmlFor="title" className="mb-1 block">
          Title
        </Label>
        <Input
          id="title"
          type="text"
          placeholder="e.g., Netflix, Gym"
          {...register("title")}
        />
        {errors.title && (
          <p className="text-sm text-red-500">{errors.title.message}</p>
        )}
      </div>

      {/* Amount */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="amount" className="mb-1 block">
            Amount
          </Label>
          <Input
            id="amount"
            type="number"
            step="0.01"
            placeholder="0.00"
            {...register("amount", {
              setValueAs: (v) => (v === "" ? undefined : Number(v)),
            })}
          />
          {errors.amount && (
            <p className="text-sm text-red-500">
              {errors.amount.message as string}
            </p>
          )}
        </div>
      </div>

      {/* Dates */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="startDate" className="mb-1 block">
            Start date
          </Label>
          <Input id="startDate" type="date" {...register("startDate")} />
          {errors.startDate && (
            <p className="text-sm text-red-500">{errors.startDate.message}</p>
          )}
        </div>
        {/* <div>
          <Label htmlFor="endDate" className="mb-1 block">
            End date (optional)
          </Label>
          <Input id="endDate" type="date" {...register("endDate")} />
        </div> */}
      </div>

      {/* Recurrence */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label className="mb-1 block">Recurrence</Label>
          <Controller
            name="recurrenceType"
            control={control}
            render={({ field }) => (
              <SimpleSelect
                value={field.value}
                onValueChange={field.onChange}
                options={[
                  { label: "Weekly", value: "weekly" },
                  { label: "Monthly", value: "monthly" },
                  { label: "Yearly", value: "yearly" },
                  { label: "Custom", value: "custom" },
                ]}
              />
            )}
          />
          {errors.recurrenceType && (
            <p className="text-sm text-red-500">
              {errors.recurrenceType.message}
            </p>
          )}
        </div>
      </div>

      <Button type="submit" className="w-full">
        Save Subscription
      </Button>
    </form>
  );
}

export default AddSubscriptionForm;
